if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      const mainVariantId = Number.parseInt(formData.get('id'), 10);
      const mainQuantity = Number.parseInt(formData.get('quantity') || '1', 10);
      const configurationOptions = [
        ...this.form.querySelectorAll('[data-configuration-option]:checked')
      ];
      const configurationQuantity = (option) => {
        const mode = option.dataset.configurationQuantityMode || 'inherit';
        if (mode === 'singleton') return 1;
        if (mode === 'multiple') {
          const quantityInput = option
            .closest('[data-configuration-option-shell]')
            ?.querySelector('[data-configuration-quantity]');
          return Math.max(0, Number.parseInt(quantityInput?.value || '0', 10) || 0);
        }
        const multiplier = Math.min(99, Math.max(
          1, Number.parseInt(option.dataset.configurationQuantityMultiplier || '1', 10) || 1
        ));
        return mainQuantity * multiplier;
      };
      const selectedConfigurationOptions = configurationOptions.filter(
        (option) => configurationQuantity(option) > 0
      );
      const selectedGroups = new Set();
      let hasDuplicateConfigurationGroup = false;
      selectedConfigurationOptions.forEach((option) => {
        const group = option.dataset.configurationGroup;
        if (!group) return;
        if (selectedGroups.has(group)) {
          hasDuplicateConfigurationGroup = true;
          return;
        }
        selectedGroups.add(group);
      });
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      let fetchRequestOpts;
      if (selectedConfigurationOptions.length) {
        const configurationId = window.crypto?.randomUUID
          ? window.crypto.randomUUID()
          : `mrg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const properties = {};
        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)]$/);
          if (match && value !== '') properties[match[1]] = value;
        });
        const configurationLabel = selectedConfigurationOptions
          .map((option) => `${option.dataset.configurationTitle} × ${configurationQuantity(option)}`)
          .filter(Boolean)
          .join(', ');
        fetchRequestOpts = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [
              {
                id: mainVariantId,
                quantity: mainQuantity,
                properties: {
                  ...properties,
                  _mrg_configuration: configurationLabel,
                  _mrg_configuration_id: configurationId
                }
              },
              ...selectedConfigurationOptions.map((option) => ({
                id: Number.parseInt(option.value, 10),
                quantity: configurationQuantity(option),
                properties: {
                  _mrg_configuration_id: configurationId,
                  _mrg_parent_variant_id: String(mainVariantId),
                  _mrg_accessory_group_id: option.dataset.configurationGroup || `option-${option.value}`,
                  _mrg_configuration_option: 'true',
                  _mrg_configuration_quantity_mode: option.dataset.configurationQuantityMode || 'inherit',
                  _mrg_configuration_quantity_multiplier: String(
                    (option.dataset.configurationQuantityMode || 'inherit') === 'inherit'
                      ? configurationQuantity(option) / mainQuantity : 1
                  )
                }
              }))
            ],
            sections_url: window.location.pathname,
            sections
          })
        };
      } else {
        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sections);
        fetchRequestOpts = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        };
      }

      try {
        if (hasDuplicateConfigurationGroup) {
          throw new Error('Invalid product configuration');
        }
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.values(data.errors).map((value) => (
            Array.isArray(value) ? value.join(', ') : String(value)
          ));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const addedMainItem = Array.isArray(data.items)
          ? data.items.find((item) => item.variant_id === mainVariantId)
          : data;
        const addedVariantId = addedMainItem?.variant_id || mainVariantId;
        const addedQuantity = addedMainItem?.quantity || mainQuantity;
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === addedVariantId
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: addedVariantId,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: itemInOldCart.quantity + addedQuantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: addedVariantId
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
